/**
 * AuthMethodSwitcher — shared dumb component (PR1)
 * Change: agregar-autenticacion-de-marcajes-con-tarjeta
 *
 * Renders the 2 alternate auth-method buttons for attendance flows.
 * MUST NOT import from @/modules/* — static lookup only.
 *
 * Styles extracted from web/Clock_Web.html line 2513 (error screen method buttons).
 */
import { useNavigate } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';
import type { AttendanceAction, VerificationMethod } from '@/modules/attendance/domain/entities/Timelog';

export interface AuthMethodSwitcherProps {
  currentMethod: VerificationMethod;
  action: AttendanceAction;
  /** Hex accent color — used as border, background tint, and text color. */
  accent: string;
  /** i18n translation function (pass t from useTranslation or prop drilling). */
  t: (key: string) => string;
  /** When true, navigate with { replace: true }. Default: false (push). */
  replace?: boolean;
}

// ── Route map ──────────────────────────────────────────────────────────────────
// Static lookup — keeps this component free of module-specific imports.
const METHOD_ROUTE: Record<VerificationMethod, (a: AttendanceAction) => string> = {
  facial: (a) => `/scan/${a}`,
  pin:    (a) => `/id-input/${a}`,
  nfc:    (a) => `/card-scan/${a}`,
};

// ── i18n key map ───────────────────────────────────────────────────────────────
const METHOD_LABEL_KEY: Record<VerificationMethod, string> = {
  facial: 'methods.useFace',
  pin:    'methods.usePin',
  nfc:    'methods.useCard',
};

// ── SVG icons — extracted from web/Clock_Web.html ─────────────────────────────
// `display: block` is required so the SVG contributes its own bounds to the
// inline-flex button layout (instead of inheriting baseline metrics from the
// surrounding inline content). This is what locks the icon's vertical center
// to the label's text-line center. See `auth-method-switcher` spec.
const SVG_STYLE: CSSProperties = { display: 'block' };

const METHOD_ICON: Record<VerificationMethod, ReactNode> = {
  facial: (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={SVG_STYLE}
    >
      <path d="M3 12a9 9 0 0 1 18 0 9 9 0 0 1-18 0z" />
      <circle cx="12" cy="10" r="3" />
      <path d="M7 19a5 5 0 0 1 10 0" />
    </svg>
  ),
  pin: (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={SVG_STYLE}
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  nfc: (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={SVG_STYLE}
    >
      <rect x="2" y="6" width="20" height="13" rx="2" />
      <path d="M16 12h.01M13 12h.01" />
    </svg>
  ),
};

const ALL_METHODS: VerificationMethod[] = ['facial', 'pin', 'nfc'];

export function AuthMethodSwitcher({
  currentMethod,
  action,
  accent,
  t,
  replace = false,
}: AuthMethodSwitcherProps) {
  const navigate = useNavigate();

  const alternates = ALL_METHODS.filter((m) => m !== currentMethod);

  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
      {alternates.map((method) => {
        const route = METHOD_ROUTE[method](action);
        return (
          <button
            key={method}
            type="button"
            onClick={() => {
              if (replace) {
                navigate(route, { replace: true });
              } else {
                navigate(route);
              }
            }}
            style={{
              padding: '9px 20px',
              borderRadius: 9,
              border: `1px solid ${accent}40`,
              background: `${accent}12`,
              color: accent,
              fontFamily: 'inherit',
              fontSize: 11,
              fontWeight: 700,
              // lineHeight: 1 locks the label's line box to its glyph height so
              // the SVG icon's vertical center coincides with the text's center
              // (see auth-method-switcher spec, M-lineHeight requirement).
              lineHeight: 1,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              transition: 'background .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${accent}18`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `${accent}12`;
            }}
          >
            {METHOD_ICON[method]}
            {t(METHOD_LABEL_KEY[method])}
          </button>
        );
      })}
    </div>
  );
}
